import React, { useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import HelpMessage from "../Components/HelpMessage";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import jwt_decode from "jwt-decode";

const AdminNotes = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [isAdmin, setIsAdmin] = useState(false);
  const [supplierCode, setSupplierCode] = useState(null);
  const [supplierName, setSupplierName] = useState(null);

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [subNotes, setSubNotes] = useState([]);
  const [loadingSubNotes, setLoadingSubNotes] = useState(false);
  const [mode, setMode] = useState("create");
  const [composerText, setComposerText] = useState("");
  const [detailDraft, setDetailDraft] = useState("");
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState("");
  const [expandedIncidentId, setExpandedIncidentId] = useState(null);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwt_decode(token);
        setIsAdmin(decoded.role && decoded.role.toLowerCase().includes("admin"));
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }

    const getStoredSupplier = () => {
      const stored = localStorage.getItem("currentUser");
      return stored ? stored.trim() : "";
    };

    const storedSupplier = getStoredSupplier();
    const storedSupplierName = localStorage.getItem("supplierName");
    setSupplierCode(storedSupplier);
    setSupplierName(storedSupplierName || storedSupplier);

    const handleSupplierChange = (event) => {
      const next = event?.detail?.supplier?.trim() || getStoredSupplier();
      setSupplierCode(next);
      const nextName = localStorage.getItem("supplierName");
      setSupplierName(nextName || next);
    };

    window.addEventListener("supplierContextChanged", handleSupplierChange);
    return () => {
      window.removeEventListener("supplierContextChanged", handleSupplierChange);
    };
  }, [token]);

  useEffect(() => {
    if (isAdmin && supplierCode) {
      fetchIncidents();
      fetchEventTypes();
    }
  }, [isAdmin, supplierCode]);

  // Fetch default supplier if admin has no supplier selected
  useEffect(() => {
    if (isAdmin && !supplierCode) {
      const fetchDefaultSupplier = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASEURL}/supplierNames`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const suppliers = response.data || [];
          if (suppliers.length > 0) {
            const firstSupplier = suppliers[0];
            const normalizedCode = firstSupplier.supplier?.trim() || "";
            const normalizedName = firstSupplier.name?.trim() || firstSupplier.supplier || "";
            
            localStorage.setItem("currentUser", normalizedCode);
            localStorage.setItem("supplierName", normalizedName);
            setSupplierCode(normalizedCode);
            setSupplierName(normalizedName);
            
            // Dispatch event to notify other components
            window.dispatchEvent(
              new CustomEvent("supplierContextChanged", {
                detail: {
                  supplier: normalizedCode,
                  name: normalizedName
                }
              })
            );
          }
        } catch (error) {
          console.error("Error fetching default supplier:", error);
        }
      };
      fetchDefaultSupplier();
    }
  }, [isAdmin, supplierCode, token]);

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASEURL}/api/eventTypeSupplierOptions`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setEventTypeOptions(response.data.eventType || []);
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASEURL}/api/notes?supplier=${supplierCode}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const fetchedIncidents = response.data || [];
      setIncidents(fetchedIncidents);

      if (fetchedIncidents.length > 0 && !selectedIncident) {
        const first = fetchedIncidents[0];
        setSelectedIncident(first);
        fetchSubNotes(first.indexkey);
        setDetailDraft(first.notes1 || "");
        setMode("view");
      } else if (selectedIncident) {
        // Refresh subnotes for currently selected incident
        const current = fetchedIncidents.find(i => i.Id === selectedIncident.Id);
        if (current) {
          setSelectedIncident(current);
          fetchSubNotes(current.indexkey);
        }
      }
    } catch (error) {
      console.error("Error fetching incidents:", error);
      toast.error("Unable to load incidents");
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubNotes = async (indexkey) => {
    if (!indexkey || !supplierCode) return;
    try {
      setLoadingSubNotes(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASEURL}/api/getSubNotes/${indexkey}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          params: {
            supplier: supplierCode, // Pass supplier for company filtering
          },
        }
      );
      setSubNotes(response.data.subNotes || []);
    } catch (error) {
      console.error("Error fetching subnotes:", error);
      setSubNotes([]);
    } finally {
      setLoadingSubNotes(false);
    }
  };

  const handleSelectIncident = (incident) => {
    // If clicking the same incident, toggle expansion
    if (selectedIncident?.Id === incident.Id) {
      if (expandedIncidentId === incident.Id) {
        setExpandedIncidentId(null);
      } else {
        setExpandedIncidentId(incident.Id);
        fetchSubNotes(incident.indexkey);
      }
    } else {
      // New incident selected - always expand it
      setSelectedIncident(incident);
      setMode("view");
      setComposerText("");
      setDetailDraft(incident.notes1 || "");
      setExpandedIncidentId(incident.Id);
      fetchSubNotes(incident.indexkey);
    }
  };

  const handleCreateIncident = async () => {
    if (!composerText.trim()) {
      toast.info("Add a message before saving");
      return;
    }
    if (!selectedEventType) {
      toast.info("Please select an event type");
      return;
    }
    if (!supplierCode) {
      toast.error("Supplier not selected");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASEURL}/api/notes`,
        {
          note: composerText,
          supplier: supplierCode,
          eventType: selectedEventType,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Incident created successfully");
      setComposerText("");
      setSelectedEventType("");
      setMode("view");
      fetchIncidents();
    } catch (error) {
      console.error("Error creating incident:", error);
      toast.error("Unable to create incident");
    }
  };

  const handleEditIncident = (incident, event) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedIncident(incident);
    setComposerText("");
    setDetailDraft(incident.notes1 || "");
    setMode("edit");
  };

  const handleSaveEdit = async () => {
    if (!detailDraft.trim() || !selectedIncident) {
      toast.info("Update the note before saving");
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASEURL}/api/notes/${selectedIncident.Id}`,
        {
          note: detailDraft,
          eventType: selectedIncident.type || selectedIncident.Type,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Incident updated successfully");
      setMode("view");
      fetchIncidents();
    } catch (error) {
      console.error("Error updating incident:", error);
      toast.error("Unable to update incident");
    }
  };

  const handleCancelEdit = () => {
    setMode("view");
    setDetailDraft(selectedIncident?.notes1 || "");
  };

  const handleAddSubNote = (incident, event) => {
    if (event) event.stopPropagation();
    if (!incident) {
      toast.info("Please select an incident first");
      return;
    }
    navigate(`/Notes/AddSubnote/${incident.Id}`, {
      state: { note: incident },
    });
    // Scroll to top when navigating
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleEditSubNote = (subNote, event) => {
    if (event) event.stopPropagation();
    if (!selectedIncident) return;
    navigate(`/Notes/EditSubnote/${subNote.Id}`, {
      state: { note: selectedIncident },
    });
  };

  if (!isAdmin) {
    return (
      <div className="customer-layout">
        <Navbar />
        <main className="customer-main">
          <div className="card">
            <p>Access denied. This page is only available to administrators.</p>
          </div>
        </main>
      </div>
    );
  }

  if (!supplierCode) {
    return (
      <div className="customer-layout">
        <Navbar />
        <main className="customer-main">
          <div className="card">
            <p>Please select a supplier to view notes.</p>
          </div>
        </main>
      </div>
    );
  }

  const isEditing = mode === "edit";
  const detailText = selectedIncident
    ? selectedIncident.notes1 || "No notes available"
    : "Select an incident to view details";
  const detailMeta = selectedIncident
    ? `${moment(selectedIncident.fdate).format("DD MMM YYYY")} • ${selectedIncident.descript || selectedIncident.Type || "N/A"}`
    : "";
  const isDetailEditable = mode === "edit" && !!selectedIncident;
  const detailValue = isDetailEditable ? detailDraft : selectedIncident?.notes1 || "";

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main">
        <section className="notes-composer card">
          <header className="card-header">
            <div className="header-spacer" aria-hidden="true" />
            <h1 className="card-title">Notes</h1>
            <div className="header-spacer" aria-hidden="true" />
          </header>
          {mode === "create" ? (
            <>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: 500 }}>
                  Event Type <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #ddd",
                    font: "inherit",
                  }}
                >
                  <option value="">--Select Event Type--</option>
                  {eventTypeOptions.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.descript}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                placeholder={isEditing ? "Editing incident in detail section below…" : "Type your note here"}
                value={isEditing ? "" : composerText}
                onChange={(e) => setComposerText(e.target.value)}
                rows={5}
                disabled={isEditing}
                maxLength={99}
              />
              <div className="notes-composer__actions">
                <button className="button ghost" onClick={() => {
                  setComposerText("");
                  setSelectedEventType("");
                  setMode("view");
                }}>
                  Cancel
                </button>
                <button className="button" onClick={handleCreateIncident}>
                  Create Incident
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="notes-composer__info">
                {selectedIncident
                  ? `Viewing incident: ${selectedIncident.descript || selectedIncident.Type || "N/A"}`
                  : "Select an incident from the timeline to view or create a new one"}
              </div>
              <div className="notes-composer__actions">
                <button
                  className="button"
                  onClick={() => {
                    setMode("create");
                    setComposerText("");
                    setSelectedEventType("");
                  }}
                >
                  New Incident
                </button>
              </div>
            </>
          )}
        </section>

        <section className="notes-session card">
          <header className="notes-session__header">
            <h2>Timeline</h2>
          </header>
          {loading ? (
            <div className="notes-empty">Loading incidents…</div>
          ) : incidents.length === 0 ? (
            <div className="notes-empty">No incidents recorded yet.</div>
          ) : (
            <div className="notes-timeline">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Event Type</th>
                    <th scope="col">Note</th>
                    <th scope="col">Supplier</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((incident) => {
                    const isSelected = selectedIncident?.Id === incident.Id;
                    const isExpanded = isSelected && expandedIncidentId === incident.Id;
                    const incidentSubNotes = isExpanded ? subNotes : [];
                    const isLoading = isExpanded && loadingSubNotes;
                    
                    return (
                      <React.Fragment key={incident.Id}>
                        <tr
                          className={isSelected ? "selected" : ""}
                          onClick={() => handleSelectIncident(incident)}
                        >
                          <td>{moment(incident.fdate).format("DD/MM/YYYY")}</td>
                          <td>{incident.descript || incident.Type || "N/A"}</td>
                          <td>{incident.notes1 || "No note"}</td>
                          <td>{incident.supplier || "N/A"}</td>
                          <td className="notes-row__actions">
                            <button
                              type="button"
                              className="icon-button"
                              onClick={(event) => handleEditIncident(incident, event)}
                              aria-label={`Edit incident from ${moment(incident.fdate).format("DD/MM/YYYY")}`}
                              title="Edit incident"
                            >
                              <i className="uicon-edit" />
                            </button>
                            <button
                              type="button"
                              className="icon-button"
                              onClick={(event) => handleAddSubNote(incident, event)}
                              aria-label="Add note to incident"
                              title="Add subnote"
                            >
                              <i className="uicon-add" />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="5" style={{ padding: 0, borderTop: 'none', backgroundColor: '#f9f9f9' }}>
                              <div style={{ padding: '16px', borderLeft: '4px solid #0f3d9a' }}>
                                {isLoading ? (
                                  <div style={{ textAlign: 'center', color: '#666', padding: '12px' }}>Loading notes…</div>
                                ) : incidentSubNotes.length === 0 ? (
                                  <div style={{ textAlign: 'center', color: '#666', padding: '12px' }}>No notes for this incident.</div>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr>
                                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid #ddd' }}>Date</th>
                                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid #ddd' }}>Note</th>
                                        <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid #ddd' }}>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {incidentSubNotes.map((subNote) => (
                                        <tr key={subNote.Id} style={{ borderBottom: '1px solid #eee' }}>
                                          <td style={{ padding: '8px 12px' }}>{moment(subNote.fdate).format("DD/MM/YYYY")}</td>
                                          <td style={{ padding: '8px 12px' }}>{subNote.notes || "No note"}</td>
                                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                            <button
                                              type="button"
                                              className="icon-button"
                                              onClick={(event) => handleEditSubNote(subNote, event)}
                                              aria-label={`Edit note from ${moment(subNote.fdate).format("DD/MM/YYYY")}`}
                                            >
                                              <i className="uicon-edit" />
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="notes-detail card">
          <header>
            <h2>Detail</h2>
            {detailMeta && <span className="notes-detail__meta">{detailMeta}</span>}
          </header>
          <div className="notes-detail__body">
            {selectedIncident ? (
              <textarea
                className="notes-detail__textarea"
                value={detailValue}
                onChange={(event) => {
                  if (mode === "edit") {
                    setDetailDraft(event.target.value);
                  }
                }}
                rows={6}
                disabled={!isDetailEditable}
                maxLength={99}
                aria-label="Selected incident detail"
              />
            ) : (
              <div className="notes-detail__placeholder">{detailText}</div>
            )}
          </div>
          {isDetailEditable && (
            <div className="notes-detail__actions">
              <button className="button ghost" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="button" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          )}
        </section>

        <div className="home-help">
          <HelpMessage />
        </div>
      </main>
    </div>
  );
};

export default AdminNotes;
